import { useCallback, useRef } from 'react';
import { ToursService, type ITourEventPayload } from '@services/ToursService';

const generateSessionId = (): string => {
    const arr = new Uint8Array(8);
    crypto.getRandomValues(arr);
    return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
};

export function useTourTracking() {
    const sessionMapRef = useRef<Map<string, string>>(new Map());
    const stepTimerRef = useRef<Map<string, number>>(new Map());
    const tourTimerRef = useRef<Map<string, number>>(new Map());

    const getSessionId = useCallback((tourId: string): string => {
        let sid = sessionMapRef.current.get(tourId);
        if (!sid) {
            sid = generateSessionId();
            sessionMapRef.current.set(tourId, sid);
        }
        return sid;
    }, []);

    const resetSession = useCallback((tourId: string) => {
        sessionMapRef.current.delete(tourId);
        stepTimerRef.current.delete(tourId);
        tourTimerRef.current.delete(tourId);
    }, []);

    const track = useCallback(
        (event: Omit<ITourEventPayload, 'sessionId'> & { sessionId?: string }) => {
            const payload: ITourEventPayload = {
                ...event,
                sessionId: event.sessionId || getSessionId(event.tourId),
                pageUrl: event.pageUrl || window.location.pathname,
            };
            ToursService.trackEvent(payload);
        },
        [getSessionId],
    );

    const trackTourStarted = useCallback(
        (tourId: string, isAutoStart: boolean) => {
            // Сброс предыдущей сессии этого тура (если была)
            resetSession(tourId);
            tourTimerRef.current.set(tourId, Date.now());
            stepTimerRef.current.set(tourId, Date.now());
            track({
                tourId,
                eventType: 'tour_started',
                stepIndex: 0,
                metadata: JSON.stringify({ autoStart: isAutoStart }),
            });
            track({ tourId, eventType: 'step_viewed', stepIndex: 0 });
        },
        [track, resetSession],
    );

    const trackStepViewed = useCallback(
        (tourId: string, stepIndex: number) => {
            stepTimerRef.current.set(tourId, Date.now());
            track({ tourId, eventType: 'step_viewed', stepIndex });
        },
        [track],
    );

    const trackStepCompleted = useCallback(
        (tourId: string, stepIndex: number) => {
            const startTime = stepTimerRef.current.get(tourId);
            const durationMs = startTime ? Date.now() - startTime : undefined;
            track({ tourId, eventType: 'step_completed', stepIndex, durationMs });
        },
        [track],
    );

    const trackActionCompleted = useCallback(
        (tourId: string, stepIndex: number, actionType: string) => {
            const startTime = stepTimerRef.current.get(tourId);
            const durationMs = startTime ? Date.now() - startTime : undefined;
            track({
                tourId,
                eventType: 'action_completed',
                stepIndex,
                actionType,
                durationMs,
            });
        },
        [track],
    );

    const trackActionTimeout = useCallback(
        (tourId: string, stepIndex: number, actionType: string) => {
            const startTime = stepTimerRef.current.get(tourId);
            const durationMs = startTime ? Date.now() - startTime : undefined;
            track({
                tourId,
                eventType: 'action_timeout',
                stepIndex,
                actionType,
                durationMs,
            });
        },
        [track],
    );

    const trackBranchSelected = useCallback(
        (tourId: string, stepIndex: number, branchLabel: string, targetIndex: number) => {
            track({
                tourId,
                eventType: 'branch_selected',
                stepIndex,
                branchLabel,
                branchTargetIndex: targetIndex,
            });
        },
        [track],
    );

    const trackPageNavigated = useCallback(
        (tourId: string, stepIndex: number, toUrl: string) => {
            track({
                tourId,
                eventType: 'page_navigated',
                stepIndex,
                metadata: JSON.stringify({ toUrl }),
            });
        },
        [track],
    );

    const trackTourRestored = useCallback(
        (tourId: string, stepIndex: number, pendingTimestamp?: number) => {
            const durationMs = pendingTimestamp ? Date.now() - pendingTimestamp : undefined;
            track({ tourId, eventType: 'tour_restored', stepIndex, durationMs });
        },
        [track],
    );

    const trackTourCompleted = useCallback(
        (tourId: string, totalSteps: number) => {
            const startTime = tourTimerRef.current.get(tourId);
            const durationMs = startTime ? Date.now() - startTime : undefined;
            track({
                tourId,
                eventType: 'tour_completed',
                stepIndex: totalSteps - 1,
                durationMs,
            });
            // Не сбрасываем сессию здесь — chain_offered/accepted/rejected
            // должны идти в той же сессии. Сброс произойдёт при старте
            // следующего тура (trackTourStarted) или при reject (trackChainRejected).
        },
        [track],
    );

    const trackTourSkipped = useCallback(
        (tourId: string, stepIndex: number) => {
            const startTime = tourTimerRef.current.get(tourId);
            const durationMs = startTime ? Date.now() - startTime : undefined;
            track({
                tourId,
                eventType: 'tour_skipped',
                stepIndex,
                durationMs,
            });
            resetSession(tourId);
        },
        [track, resetSession],
    );

    const trackChainOffered = useCallback(
        (tourId: string, nextTourCode: string) => {
            track({ tourId, eventType: 'chain_offered', nextTourCode });
        },
        [track],
    );

    const trackChainAccepted = useCallback(
        (tourId: string, nextTourCode: string) => {
            track({ tourId, eventType: 'chain_accepted', nextTourCode });
            // Сессия исходного тура завершена — пользователь принял цепочку
            resetSession(tourId);
        },
        [track, resetSession],
    );

    const trackChainRejected = useCallback(
        (tourId: string, nextTourCode: string) => {
            track({ tourId, eventType: 'chain_rejected', nextTourCode });
            // Сессия исходного тура завершена — пользователь отклонил цепочку
            resetSession(tourId);
        },
        [track, resetSession],
    );

    return {
        trackTourStarted,
        trackStepViewed,
        trackStepCompleted,
        trackActionCompleted,
        trackActionTimeout,
        trackBranchSelected,
        trackPageNavigated,
        trackTourRestored,
        trackTourCompleted,
        trackTourSkipped,
        trackChainOffered,
        trackChainAccepted,
        trackChainRejected,
    };
}
